import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { fetchInsights } from '@/lib/admin/admin-api';

type Valence = 'all' | 'protect' | 'improve';

export default function Insights() {
  const { orgId } = useOutletContext<{ orgId: string }>();
  const [val, setVal] = useState<Valence>('all');
  const q = useQuery({
    queryKey: ['admin', 'insights', orgId, val],
    queryFn: () => fetchInsights(orgId, val === 'all' ? {} : { valence: val }),
  });

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-2xl font-heading font-semibold">Insights</h2>
        <p className="text-sm text-muted-foreground">
          Themed findings, k-anonymized at publish. Raw excerpts are structurally excluded from this surface.
        </p>
      </div>

      <Tabs value={val} onValueChange={(v) => setVal(v as Valence)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="protect">Protect</TabsTrigger>
          <TabsTrigger value="improve">Improve</TabsTrigger>
        </TabsList>
      </Tabs>

      {q.isLoading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> :
       q.error ? <p className="text-sm text-destructive">{(q.error as Error).message}</p> :
       q.data!.length === 0 ? <p className="text-sm text-muted-foreground">No published insights yet.</p> :
       <div className="space-y-3">
         {q.data!.map((i) => (
           <Card key={i.id}>
             <CardHeader>
               <div className="flex items-center justify-between gap-3">
                 <CardTitle className="text-base font-heading">{i.theme}</CardTitle>
                 <Badge variant={i.valence === 'protect' ? 'secondary' : 'default'}>{i.valence}</Badge>
               </div>
               <CardDescription>
                 {i.what_phrase} · window {i.window_start} → {i.window_end} · k={i.k_at_publish}
               </CardDescription>
             </CardHeader>
             {i.excerpt_redacted && (
               <CardContent>
                 <p className="text-sm italic text-muted-foreground">"{i.excerpt_redacted}"</p>
               </CardContent>
             )}
           </Card>
         ))}
       </div>}
    </div>
  );
}
